import { useCallback, useEffect, useMemo, useState } from "react";
import CasaMapaAdminAnalyticsDateToolbar from "../../../components/casamapa/admin/CasaMapaAdminAnalyticsDateToolbar";
import CasaMapaAdminAnalyticsSectionShell from "../../../components/casamapa/admin/CasaMapaAdminAnalyticsSectionShell";
import {
  getCasaMapaAdminAnalyticsLeads,
  getCasaMapaAdminAnalyticsMarket,
} from "../../../api/clientCasamapaAdminAnalyticsApi";
import { useCasaMapaAdminPortal } from "../../../hooks/useCasaMapaAdminPortal";
import { useCasaMapaAnalyticsRange } from "../../../hooks/useCasaMapaAnalyticsRange";
import { useLocale, useCurrency } from "../../../intl";

function MetricCard({ label, value, hint, tone = "slate" }) {
  return (
    <article className={`cm-admin-analytics__metric cm-admin-analytics__metric--${tone}`}>
      <span className="cm-admin-analytics__metric-label">{label}</span>
      <strong className="cm-admin-analytics__metric-value">{value ?? "—"}</strong>
      {hint ? <small className="cm-admin-analytics__metric-hint">{hint}</small> : null}
    </article>
  );
}

function formatPct(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return `${(Number(value) * 100).toFixed(1)}%`;
}

export default function CasaMapaAdminInvestmentAnalysisPage() {
  const { clientViewId } = useCasaMapaAdminPortal();
  const { t } = useLocale();
  const { formatMoney } = useCurrency();
  const { range, activePreset, presets, handlePreset, updateStartDate, updateEndDate } =
    useCasaMapaAnalyticsRange(90);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [market, setMarket] = useState(null);
  const [leads, setLeads] = useState(null);

  const loadData = useCallback(async () => {
    if (!clientViewId) return;
    setLoading(true);
    setError("");
    const params = { startDate: range.startDate, endDate: range.endDate };
    try {
      const [marketBody, leadsBody] = await Promise.all([
        getCasaMapaAdminAnalyticsMarket(clientViewId, params),
        getCasaMapaAdminAnalyticsLeads(clientViewId, params),
      ]);
      setMarket(marketBody);
      setLeads(leadsBody);
    } catch (err) {
      setError(err.message || t("realEstate.casaMapa.admin.analytics.errors.load"));
      setMarket(null);
      setLeads(null);
    } finally {
      setLoading(false);
    }
  }, [clientViewId, range.endDate, range.startDate, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const saleRow = useMemo(
    () => (market?.byListingType || []).find((row) => /sale|venta/i.test(row.listingType || "")),
    [market]
  );
  const rentRow = useMemo(
    () => (market?.byListingType || []).find((row) => /rent|arriendo/i.test(row.listingType || "")),
    [market]
  );

  const grossYield = useMemo(() => {
    if (!saleRow?.medianPrice || !rentRow?.medianPrice) return null;
    const annualRent = Number(rentRow.medianPrice) * 12;
    return annualRent / Number(saleRow.medianPrice);
  }, [rentRow, saleRow]);

  const priceMomentumLabel = useMemo(() => {
    const increases = Number(market?.priceChanges?.increases || 0);
    const decreases = Number(market?.priceChanges?.decreases || 0);
    if (!increases && !decreases) return "—";
    const net = increases - decreases;
    const sign = net > 0 ? "+" : "";
    return `${sign}${net} (${increases}↑ / ${decreases}↓)`;
  }, [market]);

  const leadVolume = leads?.conversion?.leads ?? null;
  const conversionRate = leads?.conversion?.rate ?? null;

  const toolbar = (
    <CasaMapaAdminAnalyticsDateToolbar
      range={range}
      activePreset={activePreset}
      presets={presets}
      onPreset={handlePreset}
      onStartDateChange={updateStartDate}
      onEndDateChange={updateEndDate}
    />
  );

  return (
    <CasaMapaAdminAnalyticsSectionShell
      introKey="realEstate.casaMapa.admin.analytics.investment.subtitle"
      toolbar={toolbar}
      error={error}
      loading={loading}
    >
      <div className="cm-admin-analytics__metric-grid">
        <MetricCard
          label={t("realEstate.casaMapa.admin.analytics.investment.grossYield")}
          value={formatPct(grossYield)}
          hint={t("realEstate.casaMapa.admin.analytics.investment.grossYieldHint")}
          tone="teal"
        />
        <MetricCard
          label={t("realEstate.casaMapa.admin.analytics.investment.priceMomentum")}
          value={priceMomentumLabel}
          hint={t("realEstate.casaMapa.admin.analytics.investment.priceMomentumHint")}
          tone="blue"
        />
        <MetricCard
          label={t("realEstate.casaMapa.admin.analytics.investment.leadVolume")}
          value={leadVolume != null ? Number(leadVolume).toLocaleString() : "—"}
          hint={t("realEstate.casaMapa.admin.analytics.investment.leadVolumeHint")}
        />
        <MetricCard
          label={t("realEstate.casaMapa.admin.analytics.investment.conversionRate")}
          value={formatPct(conversionRate)}
          hint={t("realEstate.casaMapa.admin.analytics.investment.conversionRateHint")}
        />
      </div>

      <div className="cm-admin-analytics__split">
        <article className="cm-admin-analytics__card">
          <h4>{t("realEstate.casaMapa.admin.analytics.investment.returnInputs")}</h4>
          <dl className="cm-admin-analytics__dl">
            <div>
              <dt>{t("realEstate.casaMapa.admin.analytics.valuation.medianPrice")}</dt>
              <dd>{saleRow?.medianPrice != null ? formatMoney(saleRow.medianPrice) : "—"}</dd>
            </div>
            <div>
              <dt>{t("realEstate.casaMapa.admin.analytics.valuation.medianRent")}</dt>
              <dd>{rentRow?.medianPrice != null ? formatMoney(rentRow.medianPrice) : "—"}</dd>
            </div>
            <div>
              <dt>{t("realEstate.casaMapa.admin.analytics.market.activeListings")}</dt>
              <dd>{market?.activeListings != null ? Number(market.activeListings).toLocaleString() : "—"}</dd>
            </div>
          </dl>
        </article>

        <article className="cm-admin-analytics__card cm-admin-analytics__card--note">
          <h4>{t("realEstate.casaMapa.admin.analytics.investment.riskNotes")}</h4>
          <ul className="cm-admin-analytics__bullets">
            <li>{t("realEstate.casaMapa.admin.analytics.investment.riskSupply")}</li>
            <li>{t("realEstate.casaMapa.admin.analytics.investment.riskLiquidity")}</li>
            <li>{t("realEstate.casaMapa.admin.analytics.investment.riskDisclaimer")}</li>
          </ul>
        </article>
      </div>
    </CasaMapaAdminAnalyticsSectionShell>
  );
}
